import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db, auth } from "../firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

// ── Theme ─────────────────────────────────────────────────────────────────────
const theme = {
  light: {
    bg: "#f8fafc", bg2: "#f1f5f9", bg3: "#e2e8f0", border: "#e2e8f0",
    text1: "#0f172a", text2: "#64748b", text3: "#94a3b8", cardBg: "#ffffff",
    blue: "#2563eb", blueBg: "#eff6ff", blueBorder: "#bfdbfe", blueText: "#1d4ed8",
    green: "#16a34a", greenBg: "#f0fdf4", greenBorder: "#bbf7d0",
    amber: "#d97706", amberBg: "#fffbeb", amberBorder: "#fde68a",
    red: "#dc2626", redBg: "#fef2f2", redBorder: "#fecaca",
  },
  dark: {
    bg: "#0a0e1a", bg2: "#111827", bg3: "#1f2937", border: "#1f2937",
    text1: "#f1f5f9", text2: "#94a3b8", text3: "#475569", cardBg: "#111827",
    blue: "#60a5fa", blueBg: "#1e3a5f", blueBorder: "#1d4ed8", blueText: "#93c5fd",
    green: "#4ade80", greenBg: "#052e16", greenBorder: "#166534",
    amber: "#fbbf24", amberBg: "#2d1f00", amberBorder: "#92400e",
    red: "#f87171", redBg: "#2d1515", redBorder: "#7f1d1d",
  },
};

// ── Guide sections ─────────────────────────────────────────────────────────────
const SECTIONS = [
  {
    id: "entry", icon: "🖐️", title: "Comment entrer", color: "blue",
    steps: [
      { icon: "🖐️", title: "Empreinte digitale", desc: "Pose ton doigt enregistré sur le lecteur d'empreinte à l'entrée. Maintiens-le bien à plat jusqu'au bip de confirmation.", img: "/tutorial/fingerprint.png" },
      { icon: "🔢", title: "Code d'accès", desc: "Saisis le code WeStudy sur le pavé numérique.", code: "3578 #", img: "/tutorial/keypad.png" },
      { icon: "🚪", title: "Entre rapidement", desc: "La porte s'ouvre quelques secondes. Entre et assure-toi qu'elle se referme bien derrière toi.", img: "/tutorial/door_entry.png" },
    ],
  },
  {
    id: "closing", icon: "🔒", title: "Fermeture du centre", color: "amber",
    desc: "Si tu es le dernier à partir, voici les étapes dans l'ordre :",
    steps: [
      { icon: "💡", title: "Tour des salles", desc: "Passe dans chaque salle — vérifie que les lumières sont éteintes et ferme les portes.", img: "/tutorial/rooms.png" },
      { icon: "🚽", title: "Toilettes", desc: "Vérifie que les toilettes sont vides et ferme la porte.", img: "/tutorial/toilets.png" },
      { icon: "🍳", title: "Porte aluminium cuisine", desc: "Ferme la porte aluminium de la cuisine.", img: "/tutorial/kitchen_door.png" },
      { icon: "🔩", title: "Porte en fer", desc: "Ferme et verrouille la porte en fer.", img: "/tutorial/iron_door.png" },
      { icon: "✅", title: "Dernière porte", desc: "Assure-toi que la dernière porte d'entrée est bien fermée avant de partir.", img: "/tutorial/main_door.png" },
    ],
  },
  {
    id: "rules", icon: "📋", title: "Règles nuit", color: "red",
    steps: [
      { icon: "🤫", title: "Silence strict", desc: "Pas de bruit, pas d'appels dans les salles. Respect des autres membres.", img: "/tutorial/silence.png" },
      { icon: "🚫", title: "Ne jamais ouvrir à quiconque", desc: "Accès strictement personnel. N'ouvre JAMAIS la porte à quelqu'un d'autre — même s'il prétend être membre, même si c'est un ami, même si c'est ton frère. Chacun entre avec sa propre empreinte et son propre accès.", img: "/tutorial/no_guests.png" },
      { icon: "🔐", title: "Sécurité", desc: "Ne laisse jamais la porte d'entrée ouverte ou entrouverte.", img: "/tutorial/security.png" },
      { icon: "📵", title: "Zone calme", desc: "Pas d'appels téléphoniques dans les espaces communs.", img: "/tutorial/no_calls.png" },
    ],
  },
];

// ── Question bank (50 questions) ───────────────────────────────────────────────
const QUESTION_BANK = [
  // Entry
  { q: "Tu arrives à WeStudy à 2h du matin. Quelle est la première étape pour entrer ?", options: ["Taper le code directement", "Poser ton doigt sur le lecteur ZKTeco", "Appeler l'accueil", "Pousser la porte"], answer: 1 },
  { q: "Le lecteur ZKTeco ne reconnaît pas ton empreinte au premier essai. Que fais-tu ?", options: ["Taper le code sans empreinte", "Repositionner ton doigt bien à plat et réessayer", "Forcer la porte", "Rentrer chez toi"], answer: 1 },
  { q: "Quel est le code d'accès WeStudy pour la nuit ?", options: ["1234 #", "0000 #", "3578 #", "9999 #"], answer: 2 },
  { q: "Après avoir saisi le code, la porte s'ouvre. Que dois-tu faire ?", options: ["Laisser la porte ouverte pour le prochain", "Entrer rapidement et s'assurer que la porte se referme", "Bloquer la porte avec ton pied", "Attendre 30 secondes avant d'entrer"], answer: 1 },
  { q: "Tu as oublié ton code d'accès nuit. Que fais-tu ?", options: ["Essaies toutes les combinaisons", "Contactes WeStudy via l'app", "Forces la porte", "Attends qu'un autre membre entre"], answer: 1 },
  { q: "Ton meilleur ami t'appelle à 2h du matin, il est devant la porte et n'a pas d'accès nuit. Que fais-tu ?", options: ["Tu lui ouvres, c'est ton ami de confiance", "Tu ne lui ouvres pas — il doit avoir son propre accès enregistré", "Tu lui passes ton badge par la fenêtre", "Tu lui donnes le code pour cette fois"], answer: 1 },
  { q: "L'empreinte fonctionne mais le code ne passe pas. Quelle est la bonne réaction ?", options: ["Forcer la porte", "Contacter WeStudy immédiatement", "Essayer d'autres codes", "Attendre le matin"], answer: 1 },
  { q: "Ton frère t'appelle et dit qu'il est dehors et a besoin d'entrer juste 10 minutes. Que fais-tu ?", options: ["Tu lui ouvres, c'est la famille", "Tu refuses — personne n'entre sans son propre accès enregistré, même la famille", "Tu lui passes le code temporairement", "Tu le laisses entrer si personne ne regarde"], answer: 1 },

  // Closing
  { q: "Tu es le dernier membre à quitter WeStudy. Quelle est la première chose à faire ?", options: ["Fermer la porte principale directement", "Faire le tour de toutes les salles", "Éteindre uniquement les lumières du couloir", "Appeler l'accueil"], answer: 1 },
  { q: "Dans quel ordre fermes-tu les portes lors de la fermeture ?", options: ["Porte en fer → cuisine → dernière porte", "Tour salles → toilettes → porte alu cuisine → porte en fer → dernière porte", "Dernière porte → porte en fer → cuisine", "Toilettes → porte en fer → dernière porte"], answer: 1 },
  { q: "Tu fais le tour des salles et tu trouves une lumière allumée avec personne dedans. Que fais-tu ?", options: ["Tu laisses la lumière pour le confort", "Tu éteins la lumière et fermes la porte", "Tu attends 10 minutes", "Tu ignores"], answer: 1 },
  { q: "Quelle porte fermes-tu en premier dans la cuisine ?", options: ["La porte en fer", "La porte aluminium", "La porte principale", "Les toilettes"], answer: 1 },
  { q: "Tu penses avoir fermé toutes les portes mais tu n'es pas sûr de la dernière porte. Que fais-tu ?", options: ["Tu pars quand même", "Tu retournes vérifier", "Tu appelles quelqu'un", "Tu envoies un message WhatsApp"], answer: 1 },
  { q: "Un membre te dit qu'il arrive dans 10 minutes et te demande d'attendre avant de fermer. Que fais-tu ?", options: ["Tu attends indéfiniment", "Tu vérifies s'il est autorisé accès nuit, sinon tu fermes", "Tu laisses la porte ouverte", "Tu lui donnes le code"], answer: 1 },
  { q: "Lors de la fermeture tu trouves les toilettes occupées. Que fais-tu ?", options: ["Tu fermes quand même la porte", "Tu attends que la personne sorte puis fermes", "Tu ignores les toilettes", "Tu frappes et pars"], answer: 1 },
  { q: "Tu as oublié de fermer la porte aluminium de la cuisine avant de partir. Que fais-tu ?", options: ["Tu rentres chez toi, c'est pas grave", "Tu retournes fermer immédiatement", "Tu envoies un message le lendemain", "Tu attends que quelqu'un d'autre le fasse"], answer: 1 },

  // Deposit

  // Rules
  { q: "Il est 1h du matin et tu reçois un appel urgent. Que fais-tu ?", options: ["Tu décroches dans la salle silent", "Tu sors dans un espace approprié ou à l'extérieur", "Tu mets le haut-parleur", "Tu ignores l'appel"], answer: 1 },
  { q: "Un membre fait du bruit à 2h du matin. Que fais-tu ?", options: ["Tu lui parles poliment pour lui demander de se calmer", "Tu cries plus fort pour couvrir le bruit", "Tu ignores", "Tu pars immédiatement"], answer: 0 },
  { q: "Tu veux travailler avec un ami la nuit mais il n'a pas d'accès nuit WeStudy. Que fais-tu ?", options: ["Tu lui permets d'entrer discrètement", "Tu travailles seul et l'informes de faire une demande d'accès", "Tu lui donnes ton empreinte", "Tu laisses la porte entrouverte"], answer: 1 },
  { q: "Tu remarques qu'une porte d'entrée est restée ouverte en pleine nuit. Que fais-tu ?", options: ["Tu l'ignores, ce n'est pas ton problème", "Tu la fermes immédiatement et alertes WeStudy", "Tu attends que quelqu'un d'autre la ferme", "Tu prends une photo et pars"], answer: 1 },
  { q: "Quelle règle s'applique dans tous les espaces communs après minuit ?", options: ["Musique douce autorisée", "Silence strict — pas d'appels ni de bruit", "Discussions en groupe autorisées", "Pas de règles particulières"], answer: 1 },
  { q: "Tu es à WeStudy la nuit et tu entends quelqu'un frapper fort à la porte. Que fais-tu ?", options: ["Tu ouvres directement", "Tu n'ouvres pas et contactes WeStudy si nécessaire", "Tu cries pour demander qui c'est", "Tu ignores"], answer: 1 },
  { q: "Un inconnu frappe à la porte et dit être membre WeStudy mais a oublié son code. Que fais-tu ?", options: ["Tu lui ouvres car il dit être membre", "Tu n'ouvres pas — chaque membre doit entrer avec sa propre empreinte et code", "Tu lui donnes le code pour dépanner", "Tu vérifies sa carte d'identité avant d'ouvrir"], answer: 1 },
  { q: "Quelqu'un te suit discrètement et entre avec toi dans WeStudy sans t'en parler. Que fais-tu ?", options: ["Tu ignores, il a l'air normal", "Tu demandes immédiatement à cette personne de partir et alertes WeStudy", "Tu lui demandes son nom", "Tu continues ta session"], answer: 1 },
  { q: "Ta sœur t'appelle, elle veut juste récupérer ses affaires à l'intérieur à 3h du matin. Que fais-tu ?", options: ["Tu lui ouvres rapidement, c'est 2 minutes", "Tu refuses — aucune exception, elle revient pendant les heures normales", "Tu lui envoies le code", "Tu la laisses entrer si tu restes avec elle"], answer: 1 },
  { q: "Un autre membre te demande de lui prêter ton code d'accès pour une nuit. Que réponds-tu ?", options: ["OK si tu le connais bien", "Non, le code est strictement personnel", "OK si c'est une urgence", "Tu lui envoies le code par SMS"], answer: 1 },

  // Scenarios mixtes
  { q: "Tu finis ta session à 4h du matin et tu es le seul dans le centre. Quelle est ta première action avant de partir ?", options: ["Partir immédiatement", "Faire le tour complet du centre avant de fermer", "Éteindre uniquement les lumières du couloir", "Envoyer un message WhatsApp"], answer: 1 },
  { q: "La lumière d'une salle ne s'éteint pas lors de ta ronde de fermeture. Que fais-tu ?", options: ["Tu laisses la lumière allumée et pars", "Tu signales le problème à WeStudy et fermes quand même la salle", "Tu retires le fusible", "Tu reviens le lendemain régler ça"], answer: 1 },
  { q: "Pendant ta session nuit, tu trouves du matériel WeStudy laissé en désordre. Que fais-tu ?", options: ["Tu laisses comme c'est", "Tu ranges proprement et signales si quelque chose est cassé", "Tu prends une photo pour rigoler", "Tu rentres chez toi"], answer: 1 },
  { q: "Tu réalises en partant que tu n'as pas fermé les toilettes. La porte en fer est déjà fermée. Que fais-tu ?", options: ["Tu pars, c'est pas grave", "Tu rouvres, fermes les toilettes, puis refermes tout dans l'ordre", "Tu envoies un message le lendemain", "Tu laisses la porte en fer ouverte"], answer: 1 },
  { q: "Un incident de sécurité se produit la nuit (intrusion, problème). Quelle est la première chose à faire ?", options: ["Régler le problème toi-même", "Contacter immédiatement WeStudy via l'app ou appeler", "Poster sur les réseaux sociaux", "Attendre le matin"], answer: 1 },
  { q: "Tu veux inviter plusieurs amis pour une session de nuit collective. Est-ce autorisé ?", options: ["Oui si tu paies pour eux", "Non, chaque personne doit avoir son propre accès nuit autorisé", "Oui tant que c'est calme", "Oui le week-end uniquement"], answer: 1 },
  { q: "Il est interdit de faire quoi avec le code d'accès nuit WeStudy ?", options: ["Le mémoriser", "Le partager avec d'autres personnes", "Le saisir sur le pavé", "L'utiliser après minuit"], answer: 1 },
  { q: "Comment s'appelle le lecteur d'empreinte utilisé à l'entrée WeStudy ?", options: ["FingerprintPro", "ZKTeco MA300", "BioPad X1", "SecureAccess 5000"], answer: 1 },
  { q: "Tu arrives et la porte est déjà ouverte alors que tu es supposé être le premier. Que fais-tu ?", options: ["Tu entres normalement", "Tu n'entres pas et alertes WeStudy immédiatement", "Tu fermes et ouvres avec ton empreinte", "Tu attends 10 minutes"], answer: 1 },
  { q: "Quelle est la conséquence si tu laisses entrer une personne non autorisée ?", options: ["Aucune conséquence", "Ton accès nuit peut être révoqué", "Une simple remarque", "Tu perds ton dépôt seulement"], answer: 1 },
  { q: "Tu as réussi le QCM mais tu réalises que tu n'as pas compris la règle de fermeture. Que fais-tu ?", options: ["Tu passes quand même ta demande", "Tu relis le guide avant de faire ta demande", "Tu demandes à quelqu'un d'autre de fermer à ta place", "Rien, tu gères le soir même"], answer: 1 },
  { q: "La porte en fer est difficile à fermer à clé. Que fais-tu ?", options: ["Tu laisses sans fermer à clé", "Tu insistes correctement ou contactes WeStudy pour signaler le problème", "Tu bloques avec une chaise", "Tu pars sans fermer"], answer: 1 },
  { q: "Pendant la fermeture, tu trouves un objet perdu dans une salle. Que fais-tu ?", options: ["Tu le prends chez toi", "Tu le déposes à l'accueil ou le signales via l'app Lost & Found", "Tu le laisses là", "Tu le jettes"], answer: 1 },
  { q: "Tu dois partir en urgence à 3h mais tu n'as pas fini la procédure de fermeture. Que fais-tu ?", options: ["Tu pars directement", "Tu contactes WeStudy pour les prévenir avant de partir", "Tu laisses la porte ouverte", "Tu réveilles un autre membre"], answer: 1 },
  { q: "Quel est le délai de blocage si tu rates le QCM d'accès nuit ?", options: ["1 heure", "6 heures", "12 heures", "24 heures"], answer: 3 },
  { q: "Après avoir fermé la porte aluminium de la cuisine, quelle est l'étape suivante ?", options: ["Partir directement", "Fermer la porte en fer", "Éteindre les lumières du couloir", "Appeler WeStudy"], answer: 1 },
  { q: "Tu réalises qu'une fenêtre est restée ouverte en faisant ta ronde de fermeture. Que fais-tu ?", options: ["Tu pars sans la fermer", "Tu la fermes et continues ta ronde", "Tu appelles quelqu'un pour venir la fermer", "Tu ignores, les fenêtres ne comptent pas"], answer: 1 },
];

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// ── Section accordion ─────────────────────────────────────────────────────────
function SectionCard({ section, t, dark }) {
  const [open, setOpen] = useState(false);
  const accentBg  = t[`${section.color}Bg`]     || t.bg2;
  const accentBdr = t[`${section.color}Border`]  || t.border;
  const accentClr = t[section.color]              || t.blue;

  return (
    <div style={{ background: t.cardBg, border: `1.5px solid ${t.border}`, borderRadius: 16, overflow: "hidden" }}>
      <button onClick={() => setOpen(o => !o)} style={{
        width: "100%", padding: "16px", display: "flex", alignItems: "center",
        gap: 12, background: "none", border: "none", cursor: "pointer", textAlign: "left",
      }}>
        <div style={{
          width: 42, height: 42, borderRadius: 12, flexShrink: 0,
          background: accentBg, border: `1px solid ${accentBdr}`,
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20,
        }}>{section.icon}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: t.text1 }}>{section.title}</div>
          <div style={{ fontSize: 11, color: t.text3, marginTop: 1 }}>{section.steps.length} étapes</div>
        </div>
        <div style={{ fontSize: 18, color: t.text3, transform: open ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>›</div>
      </button>

      {open && (
        <div style={{ padding: "0 16px 16px", borderTop: `1px solid ${t.border}` }}>
          {section.desc && <div style={{ fontSize: 12, color: t.text2, padding: "12px 0 8px", lineHeight: 1.6 }}>{section.desc}</div>}
          {section.steps.map((step, i) => (
            <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start", marginTop: 14 }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: "50%",
                  background: accentBg, border: `1.5px solid ${accentBdr}`,
                  display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13,
                }}>{step.icon}</div>
                {i < section.steps.length - 1 && <div style={{ width: 1, height: 18, background: t.border, marginTop: 4 }} />}
              </div>
              <div style={{ flex: 1, paddingTop: 3 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: t.text1, marginBottom: 2 }}>{step.title}</div>
                <div style={{ fontSize: 12, color: t.text2, lineHeight: 1.6 }}>{step.desc}</div>
                {step.code && (
                  <div style={{
                    display: "inline-flex", alignItems: "center", gap: 8, marginTop: 8,
                    background: dark ? "#0f172a" : "#f8fafc",
                    border: `1.5px solid ${accentBdr}`, borderRadius: 10, padding: "8px 14px",
                  }}>
                    <span style={{ fontSize: 10, color: t.text3, fontWeight: 600, letterSpacing: "0.08em" }}>CODE</span>
                    <span style={{ fontSize: 22, fontWeight: 900, color: accentClr, fontFamily: "monospace", letterSpacing: "0.15em" }}>{step.code}</span>
                  </div>
                )}
                {step.img && (
                  <div style={{ marginTop: 10, borderRadius: 10, overflow: "hidden", border: `1px solid ${t.border}` }}>
                    <img src={step.img} alt={step.title}
                      style={{ width: "100%", display: "block", maxHeight: 160, objectFit: "cover" }}
                      onError={e => { e.target.parentElement.style.display = "none"; }}
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── QCM Component ─────────────────────────────────────────────────────────────
function QCM({ t, onPass, blockedUntil, setBlockedUntil }) {
  const [questions]  = useState(() => shuffle(QUESTION_BANK).slice(0, 10));
  const [current, setCurrent]   = useState(0);
  const [selected, setSelected] = useState(null);
  const [answers, setAnswers]   = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore]       = useState(0);

  const now = Date.now();
  const blocked = blockedUntil && now < blockedUntil;
  const remainingMs = blockedUntil ? Math.max(0, blockedUntil - now) : 0;
  const remainingH  = Math.floor(remainingMs / 3600000);
  const remainingM  = Math.floor((remainingMs % 3600000) / 60000);

  const handleSelect = (idx) => {
    if (selected !== null) return;
    setSelected(idx);
  };

  const handleNext = () => {
    const correct = selected === questions[current].answer;
    const newAnswers = [...answers, { correct, selected, question: questions[current] }];
    setAnswers(newAnswers);

    if (current + 1 < questions.length) {
      setCurrent(c => c + 1);
      setSelected(null);
    } else {
      const finalScore = newAnswers.filter(a => a.correct).length;
      setScore(finalScore);
      setShowResult(true);
      if (finalScore < 10) {
        const until = Date.now() + 24 * 60 * 60 * 1000;
        setBlockedUntil(until);
        const user = auth.currentUser;
        if (user) {
          setDoc(doc(db, "late_access_quiz", user.uid), {
            blockedUntil: until, lastScore: finalScore, updatedAt: serverTimestamp(),
          });
        }
      } else {
        onPass();
      }
    }
  };

  if (blocked) {
    return (
      <div style={{ textAlign: "center", padding: "24px 0" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
        <div style={{ fontSize: 18, fontWeight: 800, color: t.text1, marginBottom: 8 }}>Accès temporairement bloqué</div>
        <div style={{ fontSize: 13, color: t.text2, lineHeight: 1.7, marginBottom: 16 }}>
          Tu as raté le QCM. Reprends le guide attentivement et réessaie dans :
        </div>
        <div style={{
          display: "inline-block", background: t.redBg, border: `1.5px solid ${t.redBorder}`,
          borderRadius: 14, padding: "12px 24px",
          fontSize: 22, fontWeight: 900, color: t.red, fontFamily: "monospace",
        }}>
          {String(remainingH).padStart(2, "0")}h {String(remainingM).padStart(2, "0")}m
        </div>
      </div>
    );
  }

  if (showResult) {
    return (
      <div style={{ textAlign: "center", padding: "16px 0" }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>{score === 10 ? "🎉" : "😔"}</div>
        <div style={{ fontSize: 18, fontWeight: 800, color: t.text1, marginBottom: 8 }}>
          {score === 10 ? "Parfait !" : `${score}/10 — Essai raté`}
        </div>
        {score < 10 && (
          <div style={{ fontSize: 13, color: t.text2, lineHeight: 1.7 }}>
            Tu dois obtenir 10/10. Relis attentivement le guide.<br />
            Tu pourras réessayer dans <strong style={{ color: t.red }}>24 heures</strong>.
          </div>
        )}
        {/* Wrong answers review */}
        {score < 10 && (
          <div style={{ marginTop: 20, textAlign: "left" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: t.text2, marginBottom: 10 }}>❌ Questions ratées :</div>
            {answers.filter(a => !a.correct).map((a, i) => (
              <div key={i} style={{
                background: t.redBg, border: `1px solid ${t.redBorder}`,
                borderRadius: 10, padding: "10px 12px", marginBottom: 8,
              }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: t.text1, marginBottom: 4 }}>{a.question.q}</div>
                <div style={{ fontSize: 11, color: t.green }}>✅ {a.question.options[a.question.answer]}</div>
                <div style={{ fontSize: 11, color: t.red }}>❌ {a.question.options[a.selected]}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  const q = questions[current];
  const progress = ((current) / questions.length) * 100;

  return (
    <div>
      {/* Progress */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: t.text2 }}>Question {current + 1} / {questions.length}</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: t.blue }}>{Math.round(progress)}%</span>
        </div>
        <div style={{ height: 6, background: t.bg3, borderRadius: 6, overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${progress}%`, background: t.blue, borderRadius: 6, transition: "width 0.3s" }} />
        </div>
      </div>

      {/* Question */}
      <div style={{
        fontSize: 14, fontWeight: 700, color: t.text1,
        lineHeight: 1.6, marginBottom: 16, padding: "14px",
        background: t.bg2, borderRadius: 12,
      }}>
        {q.q}
      </div>

      {/* Options */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
        {q.options.map((opt, idx) => {
          const isSelected = selected === idx;
          const isCorrect  = selected !== null && idx === q.answer;
          const isWrong    = selected !== null && isSelected && idx !== q.answer;

          return (
            <button key={idx} onClick={() => handleSelect(idx)} style={{
              padding: "12px 14px", borderRadius: 12, border: `1.5px solid`,
              borderColor: isCorrect ? t.green : isWrong ? t.red : isSelected ? t.blue : t.border,
              background: isCorrect ? t.greenBg : isWrong ? t.redBg : isSelected ? t.blueBg : t.cardBg,
              color: isCorrect ? t.green : isWrong ? t.red : isSelected ? t.blue : t.text1,
              fontSize: 13, fontWeight: isSelected ? 700 : 500,
              cursor: selected !== null ? "default" : "pointer",
              textAlign: "left", transition: "all 0.15s",
              display: "flex", alignItems: "center", gap: 10,
            }}>
              <span style={{
                width: 22, height: 22, borderRadius: "50%", flexShrink: 0,
                background: isCorrect ? t.green : isWrong ? t.red : isSelected ? t.blue : t.bg3,
                color: isCorrect || isWrong || isSelected ? "#fff" : t.text3,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 11, fontWeight: 700,
              }}>
                {isCorrect ? "✓" : isWrong ? "✗" : String.fromCharCode(65 + idx)}
              </span>
              {opt}
            </button>
          );
        })}
      </div>

      <button
        onClick={handleNext}
        disabled={selected === null}
        style={{
          width: "100%", padding: "13px", borderRadius: 12, border: "none",
          background: selected !== null ? t.blue : t.bg3,
          color: selected !== null ? "#fff" : t.text3,
          fontSize: 14, fontWeight: 700,
          cursor: selected !== null ? "pointer" : "default",
          transition: "background 0.2s",
        }}
      >
        {current + 1 === questions.length ? "Terminer le QCM" : "Question suivante →"}
      </button>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function LateAccessTutorial() {
  const navigate = useNavigate();
  const [dark, setDark]           = useState(() => localStorage.getItem("ws-theme") === "dark");
  const [view, setView]           = useState("guide");   // guide | quiz | passed
  const [checked, setChecked]     = useState(false);
  const [blockedUntil, setBlockedUntil] = useState(null);
  const [loadingBlock, setLoadingBlock] = useState(true);

  const t = dark ? theme.dark : theme.light;

  const toggleTheme = () => setDark(d => {
    localStorage.setItem("ws-theme", !d ? "dark" : "light");
    return !d;
  });

  // Check if user already blocked or passed
  useEffect(() => {
    const check = async () => {
      const user = auth.currentUser;
      if (!user) { setLoadingBlock(false); return; }
      const snap = await getDoc(doc(db, "late_access_quiz", user.uid));
      if (snap.exists()) {
        const data = snap.data();
        if (data.passed) { setView("passed"); }
        else if (data.blockedUntil && data.blockedUntil > Date.now()) {
          setBlockedUntil(data.blockedUntil);
          setView("quiz");
        }
      }
      setLoadingBlock(false);
    };
    check();
  }, []);

  const handlePass = async () => {
    const user = auth.currentUser;
    if (user) {
      await setDoc(doc(db, "late_access_quiz", user.uid), {
        passed: true, passedAt: serverTimestamp(), blockedUntil: null,
      });
    }
    setView("passed");
  };

  if (loadingBlock) return null;

  return (
    <div style={{ background: t.bg, minHeight: "100vh", fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        @keyframes fadeIn { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:none} }
      `}</style>

      <div style={{ maxWidth: 480, margin: "0 auto", padding: "0 18px 48px" }}>

        {/* Topbar */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 16, marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src="/logo.PNG" alt="WeStudy" style={{ height: 32, objectFit: "contain" }} onError={e => e.target.style.display = "none"} />
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: t.text1 }}>Accès Nuit</div>
              <div style={{ fontSize: 11, color: t.text2 }}>Tutoriel & QCM obligatoire</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button onClick={toggleTheme} style={{
              width: 36, height: 20, borderRadius: 20, cursor: "pointer",
              position: "relative", background: t.bg3, border: `1px solid ${t.border}`,
            }}>
              <div style={{
                width: 14, height: 14, borderRadius: "50%", background: t.blue,
                position: "absolute", top: 2, left: 2, transition: "transform 0.2s",
                transform: dark ? "translateX(16px)" : "translateX(0)",
              }} />
            </button>
            <button onClick={() => navigate("/")} style={{
              fontSize: 12, borderRadius: 8, padding: "6px 10px", cursor: "pointer",
              background: t.bg2, border: `1px solid ${t.border}`, color: t.text2,
            }}>← Retour</button>
          </div>
        </div>

        {/* Tab switcher */}
        {view !== "passed" && (
          <div style={{
            display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8,
            background: t.bg2, borderRadius: 14, padding: 4, marginBottom: 20,
          }}>
            {["guide", "quiz"].map(tab => (
              <button key={tab} onClick={() => setView(tab)} style={{
                padding: "10px", borderRadius: 10, border: "none",
                background: view === tab ? t.cardBg : "transparent",
                color: view === tab ? t.text1 : t.text3,
                fontSize: 13, fontWeight: view === tab ? 700 : 500,
                cursor: "pointer", boxShadow: view === tab ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
                transition: "all 0.15s",
              }}>
                {tab === "guide" ? "📖 Guide" : "📝 QCM (10/10 requis)"}
              </button>
            ))}
          </div>
        )}

        {/* ── GUIDE ── */}
        {view === "guide" && (
          <div style={{ animation: "fadeIn 0.3s ease" }}>
            <div style={{
              background: dark ? "linear-gradient(135deg,#1e1b4b,#0f172a)" : "linear-gradient(135deg,#eff6ff,#f0fdf4)",
              border: `1.5px solid ${t.border}`, borderRadius: 18, padding: "20px", marginBottom: 16,
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: t.text3, letterSpacing: "0.1em", marginBottom: 6 }}>AVANT DE FAIRE TA DEMANDE</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: t.text1, marginBottom: 6 }}>🌙 Lis ce guide en entier</div>
              <div style={{ fontSize: 12, color: t.text2, lineHeight: 1.7 }}>
                Ce guide couvre tout ce que tu dois savoir : comment entrer, fermer le centre, gérer ton dépôt, et les règles à respecter la nuit. Ensuite tu devras réussir un QCM à <strong style={{ color: t.text1 }}>10/10</strong> pour valider ta demande.
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
              {SECTIONS.map(s => <SectionCard key={s.id} section={s} t={t} dark={dark} />)}
            </div>

            <div style={{ background: t.redBg, border: `1px solid ${t.redBorder}`, borderRadius: 14, padding: "14px", marginBottom: 16, display: "flex", gap: 10, alignItems: "center" }}>
              <span style={{ fontSize: 22, flexShrink: 0 }}>🆘</span>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: t.red, marginBottom: 2 }}>Urgence nuit</div>
                <div style={{ fontSize: 11, color: t.text2, lineHeight: 1.5 }}>En cas de problème, contacte l'équipe WeStudy via le chat de l'application.</div>
              </div>
            </div>

            <div style={{ background: t.cardBg, border: `1.5px solid ${t.border}`, borderRadius: 14, padding: "16px", marginBottom: 16 }}>
              <label style={{ display: "flex", gap: 12, alignItems: "flex-start", cursor: "pointer" }}>
                <div onClick={() => setChecked(c => !c)} style={{
                  width: 20, height: 20, borderRadius: 6, flexShrink: 0,
                  border: `2px solid ${checked ? t.green : t.border}`,
                  background: checked ? t.greenBg : "transparent",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  marginTop: 1, transition: "all 0.15s", cursor: "pointer",
                }}>
                  {checked && <span style={{ fontSize: 12, color: t.green }}>✓</span>}
                </div>
                <div style={{ fontSize: 12, color: t.text2, lineHeight: 1.6 }}>
                  J'ai lu et compris le guide d'accès nuit WeStudy. Je m'engage à respecter les règles.
                </div>
              </label>
            </div>

            <button disabled={!checked} onClick={() => setView("quiz")} style={{
              width: "100%", padding: "15px", borderRadius: 14, border: "none",
              background: checked ? t.blue : t.bg3,
              color: checked ? "#fff" : t.text3,
              fontSize: 15, fontWeight: 700,
              cursor: checked ? "pointer" : "not-allowed", transition: "background 0.2s",
            }}>
              {checked ? "📝 Passer le QCM →" : "Coche la case pour continuer"}
            </button>
          </div>
        )}

        {/* ── QCM ── */}
        {view === "quiz" && (
          <div style={{ animation: "fadeIn 0.3s ease" }}>
            <div style={{ background: t.amberBg, border: `1px solid ${t.amberBorder}`, borderRadius: 14, padding: "12px 14px", marginBottom: 20, display: "flex", gap: 8, alignItems: "center" }}>
              <span style={{ fontSize: 16 }}>⚠️</span>
              <span style={{ fontSize: 12, color: t.amber, fontWeight: 600, lineHeight: 1.5 }}>
                Tu dois obtenir <strong>10/10</strong>. En cas d'échec, tu seras bloqué pendant <strong>24 heures</strong>.
              </span>
            </div>
            <QCM t={t} onPass={handlePass} blockedUntil={blockedUntil} setBlockedUntil={setBlockedUntil} />
          </div>
        )}

        {/* ── PASSED ── */}
        {view === "passed" && (
          <div style={{ animation: "fadeIn 0.3s ease", textAlign: "center", paddingTop: 40 }}>
            <div style={{ fontSize: 60, marginBottom: 16 }}>🎉</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: t.text1, marginBottom: 8 }}>Tutoriel validé !</div>
            <div style={{ fontSize: 13, color: t.text2, lineHeight: 1.7, marginBottom: 28 }}>
              Tu as réussi le QCM avec 10/10.<br />Tu peux maintenant faire ta demande d'accès nuit.
            </div>
            <button onClick={() => navigate("/latenight")} style={{
              width: "100%", padding: "15px", borderRadius: 14, border: "none",
              background: t.green, color: "#fff", fontSize: 15, fontWeight: 700, cursor: "pointer",
            }}>
              ✅ Faire ma demande d'accès nuit →
            </button>
          </div>
        )}

      </div>
    </div>
  );
}